import { Box, CircularProgress, Typography, useTheme } from "@mui/material";
import { useInfiniteQuery } from "@tanstack/react-query";
import {
  format,
  isSameDay,
  isToday,
  isYesterday,
  parseISO,
} from "date-fns";
import { AlertTriangle, MessageSquareOff, User } from "lucide-react";
import { Fragment, useCallback, useEffect, useMemo, useRef } from "react";
import { getWhatsappConversation } from "@/modules/lead-page/api/lead-page.v2.api";
import {
  WhatsappMessage,
  WhatsappSenderRole,
} from "@/modules/lead-page/dtos/index-v2";
import ResponsiveModal from "@/modules/common/components/ui-kit/modals/responsive-modal";

interface WhatsappConversationModalProps {
  open: boolean;
  onClose: () => void;
  activityUid: string;
}

/* -------------------------------------------------------------------------- */
/*  Helpers                                                                   */
/* -------------------------------------------------------------------------- */

const formatDayLabel = (iso: string) => {
  const date = parseISO(iso);
  if (isToday(date)) return "Today";
  if (isYesterday(date)) return "Yesterday";
  return format(date, "EEEE, d MMM yyyy");
};

const getInitials = (name: string) => {
  if (!name) return "?";
  const parts = name.trim().split(/\s+/);
  const first = parts[0]?.[0] ?? "";
  const last = parts.length > 1 ? parts[parts.length - 1][0] : "";
  return (first + last).toUpperCase() || "?";
};

/** Group consecutive messages from the same sender so we can collapse
 *  avatars / sender names and keep the thread readable. */
interface MessageGroup {
  senderRole: WhatsappSenderRole;
  senderName: string;
  messages: WhatsappMessage[];
}

const groupMessages = (messages: WhatsappMessage[]): MessageGroup[] => {
  const groups: MessageGroup[] = [];
  messages.forEach((msg) => {
    const last = groups[groups.length - 1];
    const sameSender =
      last &&
      last.senderRole === msg.senderRole &&
      last.senderName === msg.senderName &&
      // split into a new group if there's a >10-minute gap,
      // this matches the mental model users have of "a burst of replies"
      Math.abs(
        parseISO(msg.time).getTime() -
          parseISO(last.messages[last.messages.length - 1].time).getTime(),
      ) <
        10 * 60 * 1000;

    if (sameSender) {
      last!.messages.push(msg);
    } else {
      groups.push({
        senderRole: msg.senderRole,
        senderName: msg.senderName,
        messages: [msg],
      });
    }
  });
  return groups;
};

/* -------------------------------------------------------------------------- */
/*  Sub-components                                                            */
/* -------------------------------------------------------------------------- */

const DateDivider = ({ iso }: { iso: string }) => {
  const theme = useTheme();
  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        py: theme.nawy.spacing.xs,
      }}
    >
      <Box
        sx={{
          px: theme.nawy.spacing.sm,
          py: "3px",
          borderRadius: "999px",
          background: theme.nawy.colors.semantic.layer.primary,
          border: `1px solid ${theme.nawy.colors.semantic.border.secondary}`,
          boxShadow: "0 1px 2px rgba(16, 24, 40, 0.04)",
        }}
      >
        <Typography
          sx={{
            ...theme.nawy.typography.english.variants.body.sm.semibold,
            color: theme.nawy.colors.semantic.text.secondary,
            letterSpacing: "0.02em",
          }}
        >
          {formatDayLabel(iso)}
        </Typography>
      </Box>
    </Box>
  );
};

const SenderAvatar = ({
  role,
  name,
}: {
  role: WhatsappSenderRole;
  name: string;
}) => {
  const theme = useTheme();
  const isAgent = role === WhatsappSenderRole.Agent;

  return (
    <Box
      aria-hidden
      sx={{
        width: 32,
        height: 32,
        borderRadius: "50%",
        flexShrink: 0,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        background: isAgent
          ? theme.nawy.colors.semantic.indicator.navyLight
          : theme.nawy.colors.semantic.indicator.greenLight,
        border: `1px solid ${
          isAgent
            ? theme.nawy.colors.semantic.indicator.navy
            : theme.nawy.colors.semantic.indicator.green
        }`,
      }}
    >
      {isAgent ? (
        <Typography
          sx={{
            ...theme.nawy.typography.english.variants.body.sm.semibold,
            color: theme.nawy.colors.semantic.indicator.navy,
            lineHeight: 1,
          }}
        >
          {getInitials(name)}
        </Typography>
      ) : (
        <User
          size={16}
          color={theme.nawy.colors.semantic.indicator.green}
          aria-hidden
        />
      )}
    </Box>
  );
};

interface MessageBubbleProps {
  msg: WhatsappMessage;
  isAgent: boolean;
  position: "only" | "first" | "middle" | "last";
}

/** Bubble corner radius follows "tail on outer-top only for the first message
 *  of a group", otherwise fully-rounded — a standard chat convention. */
const getBubbleRadius = (
  isAgent: boolean,
  position: MessageBubbleProps["position"],
) => {
  const R = "14px";
  const tail = "4px";
  const full = `${R} ${R} ${R} ${R}`;
  if (position === "middle")
    return isAgent
      ? `${R} ${tail} ${tail} ${R}`
      : `${tail} ${R} ${R} ${tail}`;
  if (position === "first")
    return isAgent
      ? `${R} ${R} ${tail} ${R}`
      : `${R} ${R} ${R} ${tail}`;
  if (position === "last")
    return isAgent
      ? `${R} ${tail} ${R} ${R}`
      : `${tail} ${R} ${R} ${R}`;
  return full;
};

const MessageBubble = ({ msg, isAgent, position }: MessageBubbleProps) => {
  const theme = useTheme();

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        alignItems: isAgent ? "flex-end" : "flex-start",
        maxWidth: "100%",
      }}
    >
      <Box
        sx={{
          maxWidth: "100%",
          px: theme.nawy.spacing.sm,
          py: theme.nawy.spacing.xs,
          borderRadius: getBubbleRadius(isAgent, position),
          background: isAgent
            ? theme.nawy.colors.semantic.indicator.greenLight
            : theme.nawy.colors.semantic.layer.primary,
          border: `1px solid ${
            isAgent
              ? theme.nawy.colors.semantic.indicator.green
              : theme.nawy.colors.semantic.border.secondary
          }`,
          boxShadow: "0 1px 1px rgba(16, 24, 40, 0.04)",
          display: "flex",
          flexDirection: "column",
          gap: "2px",
        }}
      >
        <Typography
          sx={{
            ...theme.nawy.typography.english.variants.body.md.regular,
            color: theme.nawy.colors.semantic.text.primary,
            whiteSpace: "pre-wrap",
            wordBreak: "break-word",
          }}
        >
          {msg.message}
        </Typography>

        <Typography
          component="time"
          dateTime={msg.time}
          sx={{
            ...theme.nawy.typography.english.variants.body.sm.regular,
            fontSize: "11px",
            lineHeight: 1.2,
            alignSelf: "flex-end",
            marginTop: "2px",
            color: theme.nawy.colors.semantic.text.tertiary,
          }}
        >
          {format(parseISO(msg.time), "h:mm a")}
        </Typography>
      </Box>
    </Box>
  );
};

const MessageGroupRow = ({ group }: { group: MessageGroup }) => {
  const theme = useTheme();
  const isAgent = group.senderRole === WhatsappSenderRole.Agent;

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: isAgent ? "row-reverse" : "row",
        alignItems: "flex-end",
        gap: theme.nawy.spacing.xs,
        width: "100%",
      }}
    >
      <SenderAvatar role={group.senderRole} name={group.senderName} />

      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: isAgent ? "flex-end" : "flex-start",
          gap: "2px",
          maxWidth: "72%",
          minWidth: 0,
        }}
      >
        <Typography
          sx={{
            ...theme.nawy.typography.english.variants.body.sm.semibold,
            color: theme.nawy.colors.semantic.text.secondary,
            px: "2px",
          }}
        >
          {group.senderName}
        </Typography>

        {group.messages.map((msg, idx) => {
          const position: MessageBubbleProps["position"] =
            group.messages.length === 1
              ? "only"
              : idx === 0
                ? "first"
                : idx === group.messages.length - 1
                  ? "last"
                  : "middle";
          return (
            <MessageBubble
              key={msg.id}
              msg={msg}
              isAgent={isAgent}
              position={position}
            />
          );
        })}
      </Box>
    </Box>
  );
};

/* -------------------------------------------------------------------------- */
/*  Container                                                                 */
/* -------------------------------------------------------------------------- */

const WhatsappConversationModal = ({
  open,
  onClose,
  activityUid,
}: WhatsappConversationModalProps) => {
  const theme = useTheme();
  const scrollRef = useRef<HTMLDivElement | null>(null);
  const prevScrollHeightRef = useRef<number>(0);

  const {
    data,
    isLoading,
    isError,
    refetch,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useInfiniteQuery(
    ["whatsappConversation", activityUid],
    ({ pageParam }) => getWhatsappConversation(activityUid, pageParam as string | undefined),
    {
      enabled: open,
      refetchOnWindowFocus: false,
      getNextPageParam: (lastPage) => lastPage.nextCursor ?? undefined,
    },
  );

  /** Flatten pages: pages arrive newest-first (page[0] = most recent).
   *  Reverse so oldest is at top, newest at bottom. */
  const allMessages = useMemo(() => {
    if (!data) return [];
    return [...data.pages].reverse().flatMap((p) => p?.messages ?? []).filter(Boolean);
  }, [data]);

  /** Build day-grouped sections for date dividers. */
  const sections = useMemo(() => {
    if (allMessages.length === 0) return [];
    const byDay: Array<{ dayIso: string; groups: MessageGroup[] }> = [];
    allMessages.forEach((msg) => {
      const last = byDay[byDay.length - 1];
      if (last && isSameDay(parseISO(last.dayIso), parseISO(msg.time))) {
        last.groups = groupMessages([
          ...last.groups.flatMap((g) => g.messages),
          msg,
        ]);
      } else {
        byDay.push({ dayIso: msg.time, groups: groupMessages([msg]) });
      }
    });
    return byDay;
  }, [allMessages]);

  /** Auto-scroll to bottom on first load. */
  useEffect(() => {
    if (!open || !data?.pages[0]?.messages?.length) return;
    if ((data.pages.length ?? 0) > 1) return; // only on first load
    const id = requestAnimationFrame(() => {
      const el = scrollRef.current;
      if (el) el.scrollTop = el.scrollHeight;
    });
    return () => cancelAnimationFrame(id);
  }, [open, data?.pages.length]);

  /** After loading an older page, restore scroll position so the view
   *  doesn't jump to the top. */
  useEffect(() => {
    if (isFetchingNextPage) return;
    const el = scrollRef.current;
    if (!el || prevScrollHeightRef.current === 0) return;
    el.scrollTop = el.scrollHeight - prevScrollHeightRef.current;
    prevScrollHeightRef.current = 0;
  }, [isFetchingNextPage]);

  /** Watch scroll: when near the top, fetch the next (older) page. */
  const handleScroll = useCallback(() => {
    const el = scrollRef.current;
    if (!el || !hasNextPage || isFetchingNextPage) return;
    if (el.scrollTop < 80) {
      prevScrollHeightRef.current = el.scrollHeight;
      fetchNextPage();
    }
  }, [hasNextPage, isFetchingNextPage, fetchNextPage]);

  const renderContent = () => {
    if (isLoading && allMessages.length === 0) {
      return (
        <Box
          role="status"
          aria-live="polite"
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            gap: theme.nawy.spacing.sm,
            py: theme.nawy.spacing.xl,
          }}
        >
          <CircularProgress size={28} />
          <Typography
            sx={{
              ...theme.nawy.typography.english.variants.body.sm.regular,
              color: theme.nawy.colors.semantic.text.tertiary,
            }}
          >
            Loading conversation…
          </Typography>
        </Box>
      );
    }

    if (isError) {
      return (
        <Box
          role="alert"
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            gap: theme.nawy.spacing.xs,
            py: theme.nawy.spacing.xl,
            textAlign: "center",
          }}
        >
          <AlertTriangle
            size={28}
            color={theme.nawy.colors.semantic.indicator.red}
            aria-hidden
          />
          <Typography
            sx={{
              ...theme.nawy.typography.english.variants.body.md.semibold,
              color: theme.nawy.colors.semantic.text.primary,
            }}
          >
            Couldn&apos;t load this conversation
          </Typography>
          <Typography
            sx={{
              ...theme.nawy.typography.english.variants.body.sm.regular,
              color: theme.nawy.colors.semantic.text.tertiary,
              maxWidth: 280,
            }}
          >
            Something went wrong on our end. Try again in a moment.
          </Typography>
          <Typography
            component="button"
            onClick={() => refetch()}
            sx={{
              mt: theme.nawy.spacing.xs,
              background: "transparent",
              border: "none",
              cursor: "pointer",
              ...theme.nawy.typography.english.variants.body.sm.semibold,
              color: theme.nawy.colors.semantic.indicator.navy,
              textDecoration: "underline",
              "&:hover": { opacity: 0.8 },
              "&:focus-visible": {
                outline: `2px solid ${theme.nawy.colors.semantic.indicator.navy}`,
                outlineOffset: "2px",
                borderRadius: "2px",
              },
            }}
          >
            Try again
          </Typography>
        </Box>
      );
    }

    if (!isLoading && allMessages.length === 0) {
      return (
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            gap: theme.nawy.spacing.xs,
            py: theme.nawy.spacing.xl,
            textAlign: "center",
          }}
        >
          <MessageSquareOff
            size={28}
            color={theme.nawy.colors.semantic.text.tertiary}
            aria-hidden
          />
          <Typography
            sx={{
              ...theme.nawy.typography.english.variants.body.md.semibold,
              color: theme.nawy.colors.semantic.text.primary,
            }}
          >
            No messages yet
          </Typography>
          <Typography
            sx={{
              ...theme.nawy.typography.english.variants.body.sm.regular,
              color: theme.nawy.colors.semantic.text.tertiary,
              maxWidth: 280,
            }}
          >
            This conversation doesn&apos;t contain any messages.
          </Typography>
        </Box>
      );
    }

    return (
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          gap: theme.nawy.spacing.md,
        }}
      >
        {isFetchingNextPage && (
          <Box sx={{ display: "flex", justifyContent: "center", py: theme.nawy.spacing.sm }}>
            <CircularProgress size={20} />
          </Box>
        )}
        {sections.map((section, idx) => (
          <Fragment key={`section-${idx}`}>
            <DateDivider iso={section.dayIso} />
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                gap: theme.nawy.spacing.sm,
              }}
            >
              {section.groups.map((group, gIdx) => (
                <MessageGroupRow
                  key={`group-${idx}-${gIdx}`}
                  group={group}
                />
              ))}
            </Box>
          </Fragment>
        ))}
      </Box>
    );
  };

  return (
    <ResponsiveModal
      open={open}
      onClose={onClose}
      title="WhatsApp Conversation"
      confirmButtonText="Close"
      onConfirm={onClose}
      sx={{ width: 640 }}
    >
      <Box
        ref={scrollRef}
        onScroll={handleScroll}
        sx={{
          width: "100%",
          px: theme.nawy.spacing.md,
          py: theme.nawy.spacing.sm,
          maxHeight: "60vh",
          overflowY: "auto",
          overscrollBehavior: "contain",
          background: theme.nawy.colors.semantic.layer.secondary,
          "&::-webkit-scrollbar": { width: 6 },
          "&::-webkit-scrollbar-thumb": {
            background: theme.nawy.colors.semantic.border.secondary,
            borderRadius: 3,
          },
        }}
      >
        {renderContent()}
      </Box>
    </ResponsiveModal>
  );
};

export default WhatsappConversationModal;
